/**
 * Kald til Supabase Edge Functions med ensartet fejlhåndtering,
 * timeout og typet svar. Brug ALTID denne i stedet for supabase.functions.invoke direkte.
 *
 * Serverens fejl-envelope er { error: { code, message } } — message er en
 * dansk brugervendt tekst og bruges som EdgeFunctionError.message.
 */
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client";
import { logger } from "@/lib/logger";

export class EdgeFunctionError extends Error {
  constructor(
    public fn: string,
    public status: number | undefined,
    message: string,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = "EdgeFunctionError";
  }
}

interface CallOptions {
  timeoutMs?: number;
  headers?: Record<string, string>;
}

async function extractServerError(
  fn: string,
  error: unknown
): Promise<EdgeFunctionError> {
  if (error instanceof FunctionsHttpError) {
    const status = error.context?.status;
    try {
      const body = (await error.context.json()) as {
        error?: { code?: string; message?: string };
      };
      if (body?.error?.message) {
        return new EdgeFunctionError(fn, status, body.error.message, body.error.code);
      }
    } catch {
      /* svar var ikke JSON — fald igennem til generisk fejl */
    }
    return new EdgeFunctionError(fn, status, "Serveren afviste forespørgslen");
  }
  const message = error instanceof Error ? error.message : String(error);
  return new EdgeFunctionError(fn, undefined, message);
}

export async function callFunction<TResponse, TBody = unknown>(
  fn: string,
  body?: TBody,
  opts?: CallOptions
): Promise<TResponse> {
  const timeoutMs = opts?.timeoutMs ?? 30_000;

  const invocation = (async (): Promise<TResponse> => {
    const { data, error } = await supabase.functions.invoke<TResponse>(fn, {
      body: body as Record<string, unknown> | undefined,
      headers: opts?.headers,
    });
    if (error) {
      const wrapped = await extractServerError(fn, error);
      logger.error("edge_function_error", {
        fn,
        status: wrapped.status,
        code: wrapped.code,
        error: wrapped.message,
      });
      throw wrapped;
    }
    if (data == null) throw new EdgeFunctionError(fn, undefined, "Tomt svar fra server");
    return data;
  })();

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () =>
        reject(
          new EdgeFunctionError(
            fn,
            undefined,
            "Serveren svarede ikke i tide. Tjek din forbindelse og prøv igen."
          )
        ),
      timeoutMs
    );
  });

  try {
    return await Promise.race([invocation, timeout]);
  } finally {
    clearTimeout(timer);
  }
}
